import { useEffect, useMemo, useRef, useState } from 'react';
import { Banner, BlockStack, Box, Button, FormLayout, InlineStack, Select, Text, TextField } from '@shopify/polaris';
import { ClipboardIcon, CodeIcon, DeleteIcon, PlusIcon, ResetIcon } from '@shopify/polaris-icons';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import { getRequest, postRequest } from '../../shared/components/functions.jsx';
import { getPublicAssetUrl } from '../../utils/publicAssetUrl';

// Accent for the job title and website; name and contact lines stay in the text color
const DEFAULT_SIGNATURE_COLOR = '#b6424c';
const SIGNATURE_TEXT_COLOR = '#242424';
const DEFAULT_COMPANY = {
  address: 'Vriesenrot 7, 9200 Dendermonde (BE)',
  vat: 'BE0533923038',
  phone: '+32 52 49 90 70',
  website: 'www.schuilenburg.com'
};

const FALLBACK_BUSINESS_UNIT = {
  BusinessUnitID: 'fallback-schuilenburg',
  Code: 'SCHUILENBURG',
  Name: 'Schuilenburg',
  EmailSignatureBannerUrl: '',
  EmailSignatureBannerWidth: 480,
  EmailSignatureBannerHeight: 120,
  EmailSignatureBrandColor: DEFAULT_SIGNATURE_COLOR,
  EmailSignatureWebsite: DEFAULT_COMPANY.website,
  EmailSignaturePhone: DEFAULT_COMPANY.phone,
  EmailSignatureLinks: []
};

function parseSignatureLinks(value) {
  let raw = value;
  if (!Array.isArray(raw)) {
    try {
      raw = JSON.parse(String(value || '[]'));
    } catch (error) {
      raw = [];
    }
  }

  return (Array.isArray(raw) ? raw : []).map((link) => ({ label: String(link?.label || '').trim(), url: String(link?.url || '').trim() })).filter((link) => link.label || link.url);
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cleanPhoneHref(value) {
  return `tel:${String(value || '').replace(/[^+\d]/g, '')}`;
}

function websiteHref(value) {
  if (!value) return '';
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function websiteLabel(value) {
  return String(value || '')
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
}

// "Unassigned" is a routing bucket, not something to put under a name
function signatureUnitName(businessUnit) {
  return String(businessUnit.Code || '').toUpperCase() === 'UNASSIGNED' ? FALLBACK_BUSINESS_UNIT.Name : businessUnit.Name;
}

function normalizeBusinessUnit(unit) {
  return {
    ...unit,
    BusinessUnitID: unit?.BusinessUnitID ?? unit?.value ?? FALLBACK_BUSINESS_UNIT.BusinessUnitID,
    Code: unit?.Code || unit?.code || unit?.label || FALLBACK_BUSINESS_UNIT.Code,
    Name: unit?.Name || unit?.name || unit?.label || FALLBACK_BUSINESS_UNIT.Name,
    EmailSignatureBannerUrl: unit?.EmailSignatureBannerUrl || '',
    EmailSignatureBannerWidth: Number(unit?.EmailSignatureBannerWidth || 480),
    EmailSignatureBannerHeight: Number(unit?.EmailSignatureBannerHeight || 120),
    EmailSignatureBannerAlt: String(unit?.EmailSignatureBannerAlt || '').trim(),
    EmailSignatureBrandColor: unit?.EmailSignatureBrandColor || DEFAULT_SIGNATURE_COLOR,
    EmailSignatureWebsite: unit?.EmailSignatureWebsite || '',
    EmailSignaturePhone: String(unit?.EmailSignaturePhone || '').trim(),
    EmailSignatureLinks: parseSignatureLinks(unit?.EmailSignatureLinks)
  };
}

function getBusinessUnitOptions(businessUnits) {
  const activeUnits = (Array.isArray(businessUnits) ? businessUnits : []).filter((unit) => unit?.IsActive !== false && unit?.IsActive !== 0).map(normalizeBusinessUnit);

  return activeUnits.length ? activeUnits : [FALLBACK_BUSINESS_UNIT];
}

function accountValue(accountUser, ...keys) {
  for (const key of keys) {
    if (accountUser?.[key] !== undefined && accountUser?.[key] !== null && String(accountUser[key]).trim() !== '') {
      return String(accountUser[key]).trim();
    }
  }

  return '';
}

function storageKey(accountUser) {
  return `emailSignatureBusinessUnit:${accountUser?.id || accountUser?.GebruikerID || 'current'}`;
}

function findOwnerRuleBusinessUnitId(rules, accountUser) {
  const userId = String(accountUser?.id || accountUser?.GebruikerID || '').trim();
  if (!userId) return null;

  const match = (Array.isArray(rules) ? rules : [])
    .filter((rule) => String(rule?.RuleType || '').toUpperCase() === 'OWNER_ID' && String(rule?.RuleValue || '').trim() === userId && rule?.IsActive !== false && rule?.IsActive !== 0)
    .sort((a, b) => (a.Priority || 0) - (b.Priority || 0))[0];

  return match ? String(match.BusinessUnitID) : null;
}

function preferredBusinessUnitId(accountUser, businessUnits, ownerRuleBusinessUnitId) {
  const options = getBusinessUnitOptions(businessUnits);

  // The admin-managed OWNER_ID rule in the database is the source of truth for a user's business unit
  if (ownerRuleBusinessUnitId && options.some((unit) => String(unit.BusinessUnitID) === String(ownerRuleBusinessUnitId))) return String(ownerRuleBusinessUnitId);

  const savedValue = localStorage.getItem(storageKey(accountUser));
  if (savedValue && options.some((unit) => String(unit.BusinessUnitID) === String(savedValue))) return savedValue;

  const source = `${accountUser?.ORGANISATION_NAME || ''} ${accountUser?.email || ''} ${accountUser?.Email || ''}`.toLowerCase();
  const match = options.find((unit) => source.includes(String(unit.Code || '').toLowerCase()) || source.includes(String(unit.Name || '').toLowerCase()));

  return String((match || options[0]).BusinessUnitID);
}

function getBusinessUnit(value, businessUnits) {
  const options = getBusinessUnitOptions(businessUnits);
  return options.find((unit) => String(unit.BusinessUnitID) === String(value)) || options[0];
}

function createSignatureForm(accountUser, businessUnits, ownerRuleBusinessUnitId) {
  const businessUnitId = preferredBusinessUnitId(accountUser, businessUnits, ownerRuleBusinessUnitId);
  const businessUnit = getBusinessUnit(businessUnitId, businessUnits);

  return {
    name: accountValue(accountUser, 'full_name', 'Gebruikernaam'),
    jobTitle: accountValue(accountUser, 'JobTitle'),
    businessUnitId,
    mobile: accountValue(accountUser, 'DirectPhoneLine'),
    address: DEFAULT_COMPANY.address,
    companyVat: DEFAULT_COMPANY.vat,
    companyTel: businessUnit.EmailSignaturePhone || DEFAULT_COMPANY.phone,
    website: businessUnit.EmailSignatureWebsite || DEFAULT_COMPANY.website,
    links: (businessUnit.EmailSignatureLinks || []).map((link) => ({ ...link })),
    extra: '',
    color: businessUnit.EmailSignatureBrandColor || DEFAULT_SIGNATURE_COLOR
  };
}

function makeBannerPlaceholderDataUri(businessUnit) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${businessUnit.EmailSignatureBannerWidth}" height="${
      businessUnit.EmailSignatureBannerHeight
    }"><rect width="100%" height="100%" fill="#e5e7eb"/><text x="50%" y="45%" text-anchor="middle" font-family="Arial" font-size="14" fill="#6b7280">Banner ${escapeHtml(
      businessUnit.Name
    )}</text><text x="50%" y="65%" text-anchor="middle" font-family="Arial" font-size="11" fill="#9ca3af">Upload a banner for this business unit</text></svg>`
  )}`;
}

const HTML_PIPE_SEPARATOR = ' &nbsp;|&nbsp; ';
const HTML_BULLET_SEPARATOR = ' &bull; ';

// Phone (mobile wins over the company number) • website • links, the first contact line of both signatures
function makeContactLineHtml(form, textColor) {
  const accentLinkStyle = `color: ${form.color}; text-decoration: none;`;
  const textLinkStyle = `color: ${textColor}; text-decoration: none;`;
  const phone = form.mobile || form.companyTel;
  const parts = [];
  if (phone) parts.push(`<a href="${cleanPhoneHref(phone)}" style="${textLinkStyle}">${escapeHtml(phone)}</a>`);
  if (form.website) parts.push(`<a href="${escapeHtml(websiteHref(form.website))}" style="${accentLinkStyle}">${escapeHtml(websiteLabel(form.website))}</a>`);
  (form.links || []).filter((link) => link.label && link.url).forEach((link) => parts.push(`<a href="${escapeHtml(websiteHref(link.url))}" style="${textLinkStyle}">${escapeHtml(link.label)}</a>`));

  return parts.join(HTML_BULLET_SEPARATOR);
}

function makePlainContactLine(form) {
  const links = (form.links || []).filter((link) => link.label && link.url).map((link) => `${link.label}: ${link.url}`);
  return [form.mobile || form.companyTel, websiteLabel(form.website), ...links].filter(Boolean).join(' • ');
}

function makeShortSignatureHtml(form, businessUnit) {
  const gray = '#444444';
  const light = '#777777';
  const baseStyle = `font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 16px; color: ${gray};`;
  const subtitle = [form.jobTitle, signatureUnitName(businessUnit)].filter(Boolean).map(escapeHtml).join(HTML_PIPE_SEPARATOR);
  const contactLine = makeContactLineHtml(form, gray);

  let html = `<table cellpadding="0" cellspacing="0" border="0" style="${baseStyle}">`;
  html += `<tr><td style="${baseStyle} padding-bottom: 4px;">`;
  html += `<span style="font-size: 12px; font-weight: bold; color: ${form.color};">${escapeHtml(form.name)}</span>`;
  if (subtitle) html += `<br><span style="font-size: 10px; color: ${light};">${subtitle}</span>`;
  if (contactLine) html += `<br><span style="font-size: 10px; color: ${gray};">${contactLine}</span>`;
  html += '</td></tr></table>';
  return html;
}

// Name + role, then phone/website/links and address/VAT on two short lines, then the business unit banner.
// bannerSrc swaps the public banner URL for an embedded data URL (shows without "download pictures", but frozen at copy time).
function makeSignatureHtml(form, businessUnit, variant = 'full', { bannerSrc } = {}) {
  if (variant === 'short') return makeShortSignatureHtml(form, businessUnit);

  const text = SIGNATURE_TEXT_COLOR;
  const baseStyle = `font-family: Arial, Helvetica, sans-serif; font-size: 11px; line-height: 16px; color: ${text};`;
  const subtitle = [form.jobTitle, signatureUnitName(businessUnit)].filter(Boolean).map(escapeHtml).join(' ');

  let html = `<table cellpadding="0" cellspacing="0" border="0" style="${baseStyle}">`;
  html += `<tr><td style="${baseStyle} padding-bottom: 16px;">`;
  html += `<span style="font-weight: bold; color: ${text};">${escapeHtml(form.name)}</span>`;
  if (subtitle) html += `<br><span style="color: ${form.color};">${subtitle}</span>`;
  html += '</td></tr>';

  const contactLine = makeContactLineHtml(form, text);
  const companyLine = [form.address, form.companyVat].filter(Boolean).map(escapeHtml);

  const rows = [];
  if (contactLine) rows.push(contactLine);
  if (companyLine.length) rows.push(companyLine.join(HTML_BULLET_SEPARATOR));
  if (form.extra) rows.push(escapeHtml(form.extra));

  if (rows.length) html += `<tr><td style="${baseStyle} padding-bottom: 11px;">${rows.join('<br>')}</td></tr>`;

  const bannerUrl = bannerSrc || getPublicAssetUrl(businessUnit.EmailSignatureBannerUrl);
  if (bannerUrl) {
    let image = `<img src="${escapeHtml(bannerUrl)}" width="${businessUnit.EmailSignatureBannerWidth}" height="${businessUnit.EmailSignatureBannerHeight}" alt="${escapeHtml(
      businessUnit.EmailSignatureBannerAlt || signatureUnitName(businessUnit)
    )}" style="display: block; border: 0; width: ${businessUnit.EmailSignatureBannerWidth}px; height: ${businessUnit.EmailSignatureBannerHeight}px;">`;
    if (form.website) image = `<a href="${escapeHtml(websiteHref(form.website))}" style="text-decoration: none;">${image}</a>`;
    html += `<tr><td>${image}</td></tr>`;
  }

  html += '</table>';
  return html;
}

function makePlainText(form, businessUnit, variant = 'full') {
  if (variant === 'short') {
    return [form.name, [form.jobTitle, signatureUnitName(businessUnit)].filter(Boolean).join(' | '), makePlainContactLine(form)].filter(Boolean).join('\n');
  }

  return [
    [form.name, [form.jobTitle, signatureUnitName(businessUnit)].filter(Boolean).join(' ')].filter(Boolean).join('\n'),
    [makePlainContactLine(form), [form.address, form.companyVat].filter(Boolean).join(' • '), form.extra].filter(Boolean).join('\n')
  ]
    .filter(Boolean)
    .join('\n\n');
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function copyHtmlToClipboard(html, text) {
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text], { type: 'text/plain' })
        })
      ]);
      return true;
    }
  } catch (error) {
    // Fall back to copying a rendered selection below
  }

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.innerHTML = html;
  document.body.appendChild(container);

  let copied = false;
  const selection = window.getSelection();
  if (selection) {
    const range = document.createRange();
    range.selectNodeContents(container);
    selection.removeAllRanges();
    selection.addRange(range);
    copied = document.execCommand('copy');
    selection.removeAllRanges();
  }
  container.remove();

  return copied;
}

function formatCopyDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
}

export function EmailSignatureGenerator({ accountUser, businessUnits }) {
  const [ownerRuleBusinessUnitId, setOwnerRuleBusinessUnitId] = useState(null);
  const [form, setForm] = useState(() => createSignatureForm(accountUser, businessUnits));
  const [isCustomized, setIsCustomized] = useState(false);
  const [htmlVisible, setHtmlVisible] = useState(null);
  const [status, setStatus] = useState(null);
  const [embeddedBanner, setEmbeddedBanner] = useState({ url: '', dataUrl: '' });
  const [copyStatus, setCopyStatus] = useState(null);
  const fullPreviewRef = useRef(null);
  const businessUnit = getBusinessUnit(form.businessUnitId, businessUnits);
  const fullSignatureHtml = useMemo(() => makeSignatureHtml(form, businessUnit, 'full'), [form, businessUnit]);
  const shortSignatureHtml = useMemo(() => makeSignatureHtml(form, businessUnit, 'short'), [form, businessUnit]);
  const bannerPublicUrl = getPublicAssetUrl(businessUnit.EmailSignatureBannerUrl);
  const embeddedBannerSrc = embeddedBanner.url === bannerPublicUrl ? embeddedBanner.dataUrl : '';

  useEffect(() => {
    let isMounted = true;

    async function loadOwnerRuleBusinessUnit() {
      try {
        const rules = await getRequest('/api/business_units/rules/all');
        if (isMounted) setOwnerRuleBusinessUnitId(findOwnerRuleBusinessUnitId(rules, accountUser));
      } catch (error) {
        // No rule available; the name/email heuristic in preferredBusinessUnitId still applies
      }
    }

    loadOwnerRuleBusinessUnit();

    return () => {
      isMounted = false;
    };
  }, [accountUser?.id, accountUser?.GebruikerID]);

  useEffect(() => {
    if (!isCustomized) setForm(createSignatureForm(accountUser, businessUnits, ownerRuleBusinessUnitId));
  }, [accountUser, businessUnits, isCustomized, ownerRuleBusinessUnitId]);

  useEffect(() => {
    let isMounted = true;
    getRequest('/api/business_units/signature-copies/me').then((result) => {
      if (isMounted && result && typeof result === 'object') setCopyStatus(result);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Preload the banner as a data URL so "Copy with embedded banner" can write the clipboard straight from the click
  useEffect(() => {
    if (!bannerPublicUrl) return undefined;
    let isMounted = true;

    fetch(bannerPublicUrl, { cache: 'no-cache' })
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(`Banner request failed (${response.status})`))))
      .then((blob) => (blob.type.startsWith('image/') ? blobToDataUrl(blob) : Promise.reject(new Error('Banner URL did not return an image'))))
      .then((dataUrl) => {
        if (isMounted) setEmbeddedBanner({ url: bannerPublicUrl, dataUrl });
      })
      .catch(() => {
        if (isMounted) setEmbeddedBanner({ url: bannerPublicUrl, dataUrl: '' });
      });

    return () => {
      isMounted = false;
    };
  }, [bannerPublicUrl]);

  useEffect(() => {
    const image = fullPreviewRef.current?.querySelector('img');
    if (!image) return;

    image.onerror = () => {
      image.onerror = null;
      image.src = makeBannerPlaceholderDataUri(businessUnit);
      image.dataset.bannerMissing = 'true';
    };
  }, [fullSignatureHtml, businessUnit]);

  function changeField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setIsCustomized(true);
  }

  function changeLinkField(index, key, value) {
    setForm((current) => ({ ...current, links: current.links.map((link, i) => (i === index ? { ...link, [key]: value } : link)) }));
    setIsCustomized(true);
  }

  function addLink() {
    setForm((current) => ({ ...current, links: [...current.links, { label: '', url: '' }] }));
    setIsCustomized(true);
  }

  function removeLink(index) {
    setForm((current) => ({ ...current, links: current.links.filter((link, i) => i !== index) }));
    setIsCustomized(true);
  }

  function changeBusinessUnit(value) {
    const nextBusinessUnit = getBusinessUnit(value, businessUnits);
    localStorage.setItem(storageKey(accountUser), String(value));

    setForm((current) => ({
      ...current,
      businessUnitId: value,
      color: nextBusinessUnit.EmailSignatureBrandColor || current.color || DEFAULT_SIGNATURE_COLOR,
      // Not kept from the previous unit: its number would reach the wrong team
      companyTel: nextBusinessUnit.EmailSignaturePhone || DEFAULT_COMPANY.phone,
      website: nextBusinessUnit.EmailSignatureWebsite || current.website || DEFAULT_COMPANY.website,
      links: (nextBusinessUnit.EmailSignatureLinks || []).map((link) => ({ ...link }))
    }));
    setIsCustomized(true);
  }

  function resetFromAccount() {
    setForm(createSignatureForm(accountUser, businessUnits));
    setIsCustomized(false);
    setStatus(null);
  }

  function showStatus(message, tone = 'success') {
    setStatus({ message, tone });
    window.setTimeout(() => setStatus(null), 5000);
  }

  async function recordCopy(mode) {
    const businessUnitId = Number(form.businessUnitId);
    if (!Number.isInteger(businessUnitId)) return;

    const result = await postRequest(
      '/api/business_units/signature-copies',
      { businessUnitId, mode },
      () => {},
      () => {}
    );
    if (result && typeof result === 'object') setCopyStatus(result);
  }

  async function copySignature(variant, embedBanner = false) {
    if (embedBanner && !embeddedBannerSrc) {
      showStatus('The banner could not be loaded, so it cannot be embedded. Use "Copy full signature" instead.', 'warning');
      return;
    }

    const html = embedBanner ? makeSignatureHtml(form, businessUnit, 'full', { bannerSrc: embeddedBannerSrc }) : variant === 'short' ? shortSignatureHtml : fullSignatureHtml;
    const copied = await copyHtmlToClipboard(html, makePlainText(form, businessUnit, variant));

    if (!copied) {
      showStatus('Copy failed. Select the preview and copy it manually.', 'warning');
      return;
    }

    if (variant === 'full') recordCopy(embedBanner ? 'embedded' : 'linked');

    const bannerMissing = variant === 'full' && !embedBanner && fullPreviewRef.current?.querySelector('img')?.dataset?.bannerMissing === 'true';
    if (bannerMissing) {
      showStatus('Full signature copied, but the business unit banner could not be loaded.', 'warning');
      return;
    }

    if (embedBanner) {
      showStatus("Full signature with embedded banner copied. Paste it into your mail app's signature settings, replacing the old one.");
      return;
    }

    showStatus(`${variant === 'short' ? 'Short' : 'Full'} signature copied.`);
  }

  const businessUnitOptions = getBusinessUnitOptions(businessUnits).map((unit) => ({
    label: `${unit.Code} (${unit.Name})`,
    value: String(unit.BusinessUnitID)
  }));
  const htmlValue = htmlVisible === 'short' ? shortSignatureHtml : fullSignatureHtml;

  return (
    <BlockStack gap="400">
      <CardHeading title="Email signature" />

      <FormLayout>
        <Text variant="headingSm" as="h3">
          Personal details
        </Text>
        <TextField label="Name" value={form.name} onChange={(value) => changeField('name', value)} autoComplete="off" />
        <FormLayout.Group>
          <TextField label="Job title" value={form.jobTitle} onChange={(value) => changeField('jobTitle', value)} autoComplete="off" />
          <Select label="Business unit" options={businessUnitOptions} value={String(form.businessUnitId)} onChange={changeBusinessUnit} />
        </FormLayout.Group>
        <TextField label="Mobile" value={form.mobile} onChange={(value) => changeField('mobile', value)} autoComplete="off" helpText="Replaces the company phone in the signatures." />

        <Text variant="headingSm" as="h3">
          Company details
        </Text>
        <FormLayout.Group>
          <TextField label="Company address" value={form.address} onChange={(value) => changeField('address', value)} autoComplete="off" />
          <TextField label="VAT number" value={form.companyVat} onChange={(value) => changeField('companyVat', value)} autoComplete="off" />
        </FormLayout.Group>
        <FormLayout.Group>
          <TextField label="Company phone" value={form.companyTel} onChange={(value) => changeField('companyTel', value)} autoComplete="off" />
          <TextField label="Website" value={form.website} onChange={(value) => changeField('website', value)} autoComplete="off" />
        </FormLayout.Group>
        {form.links.map((link, index) => (
          <InlineStack key={index} gap="200" blockAlign="start" wrap={false}>
            <div style={{ flex: '1 1 0' }}>
              <TextField label="Link wording" labelHidden={index > 0} value={link.label} onChange={(value) => changeLinkField(index, 'label', value)} autoComplete="off" placeholder="LinkedIn" />
            </div>
            <div style={{ flex: '2 1 0' }}>
              <TextField
                label="Link URL"
                labelHidden={index > 0}
                value={link.url}
                onChange={(value) => changeLinkField(index, 'url', value)}
                autoComplete="off"
                placeholder="www.linkedin.com/company/..."
                connectedRight={<Button icon={DeleteIcon} accessibilityLabel="Remove link" onClick={() => removeLink(index)} />}
              />
            </div>
          </InlineStack>
        ))}
        <div>
          <Button icon={PlusIcon} onClick={addLink}>
            Add link
          </Button>
        </div>

        <TextField label="Extra text" value={form.extra} onChange={(value) => changeField('extra', value)} autoComplete="off" />
        <Box>
          <Text as="label" variant="bodyMd" fontWeight="medium">
            Brand color
          </Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 6 }}>
            <input
              aria-label="Brand color"
              type="color"
              value={form.color}
              onChange={(event) => changeField('color', event.target.value)}
              style={{ width: 48, height: 36, border: '1px solid #c9cccf', borderRadius: 6, padding: 2, background: '#fff' }}
            />
            <Text as="span" tone="subdued">
              {form.color}
            </Text>
          </div>
        </Box>
      </FormLayout>

      {copyStatus?.outdated ? (
        <Banner tone="warning" title="Your email banner has changed" action={{ content: 'Copy with new banner', onAction: () => copySignature('full', true), disabled: !embeddedBannerSrc }}>
          <p>
            {`You copied your signature with an embedded banner${copyStatus.copiedAt ? ` on ${formatCopyDate(copyStatus.copiedAt)}` : ''}. ${
              copyStatus.businessUnitName || 'Your business unit'
            } has a new banner since then: copy it again and replace the signature in your mail app.`}
          </p>
        </Banner>
      ) : null}

      {!businessUnit.EmailSignatureBannerUrl ? (
        <Banner tone="warning">This business unit has no public email banner yet. Upload one in Settings &gt; Business units to include it in the full signature.</Banner>
      ) : null}

      <Box borderColor="border" borderWidth="025" borderRadius="200" padding="500" background="bg-surface">
        <BlockStack gap="400">
          <Text variant="headingSm" as="h3">
            Full signature
          </Text>
          <div ref={fullPreviewRef} style={{ minHeight: 150, overflowX: 'auto' }} dangerouslySetInnerHTML={{ __html: fullSignatureHtml }} />
          <Text as="p" tone="subdued">
            "Copy full signature" links the banner, so a new banner shows up automatically, but some mail apps (mainly classic Outlook) ask the reader to download pictures first. "Copy with embedded
            banner" puts the image inside the signature so it always shows; when the banner changes, this page and your home page remind you to copy it again. Gmail&apos;s signature settings only
            accept the linked version.
          </Text>
          <InlineStack gap="200" wrap>
            <Button icon={ClipboardIcon} variant="primary" onClick={() => copySignature('full')}>
              Copy full signature
            </Button>
            <Button icon={ClipboardIcon} onClick={() => copySignature('full', true)} disabled={!embeddedBannerSrc}>
              Copy with embedded banner
            </Button>
            <Button icon={CodeIcon} onClick={() => setHtmlVisible((visible) => (visible === 'full' ? null : 'full'))}>
              {htmlVisible === 'full' ? 'Hide full HTML' : 'Show full HTML'}
            </Button>
            <Button icon={ResetIcon} onClick={resetFromAccount}>
              Refill from account
            </Button>
          </InlineStack>
        </BlockStack>
      </Box>

      <Box borderColor="border" borderWidth="025" borderRadius="200" padding="500" background="bg-surface">
        <BlockStack gap="400">
          <Text variant="headingSm" as="h3">
            Short signature
          </Text>
          <Text as="p" tone="subdued">
            A compact reply/forward version: name, title, business unit and direct contact details, without banner or address.
          </Text>
          <div style={{ minHeight: 72, overflowX: 'auto' }} dangerouslySetInnerHTML={{ __html: shortSignatureHtml }} />
          <InlineStack gap="200" wrap>
            <Button icon={ClipboardIcon} onClick={() => copySignature('short')}>
              Copy short signature
            </Button>
            <Button icon={CodeIcon} onClick={() => setHtmlVisible((visible) => (visible === 'short' ? null : 'short'))}>
              {htmlVisible === 'short' ? 'Hide short HTML' : 'Show short HTML'}
            </Button>
          </InlineStack>
        </BlockStack>
      </Box>

      {status ? <Banner tone={status.tone}>{status.message}</Banner> : null}
      {htmlVisible ? <TextField label={htmlVisible === 'short' ? 'Short HTML' : 'Full HTML'} value={htmlValue} multiline={8} readOnly autoComplete="off" /> : null}
    </BlockStack>
  );
}
